import React, { forwardRef, useState, useMemo, useImperativeHandle, useCallback } from 'react'

import { RadioButton as Icon } from 'assets/svgIconComponents'
import { RadioButtonProps, classes } from '.'
import Button from '../Button'

export const RadioButton = React.memo(
  forwardRef<{ value: boolean | undefined }, RadioButtonProps>((props, ref) => {
    const { id, className: cn, value, defaultValue, disabled, tabbable = true, title, label, onClickBlur, onClick, onFocus } = props

    const [state, setState] = useState(Boolean(defaultValue))

    const className = useMemo(() => [classes.wrapper, cn].filter(c => c).join(' '), [cn])
    const computedState = useMemo(() => (value === undefined ? state : value), [value, state])

    const emphasis = ['emphasized', 'primary', 'secondary', 'tertiary', 'transparent'].find(prop => props[prop as keyof RadioButtonProps])
    const baselineDataSet = useMemo(() => (computedState ? { emphasis } : {}), [emphasis, computedState])
    const dataSet = useMemo(
      () => Object.assign({}, baselineDataSet, props.dataSet, { toggle: ['off', 'on'][Number(computedState)] }),
      [baselineDataSet, props.dataSet, computedState]
    )

    const onClickHandler = useCallback(() => {
      if (value === undefined) setState(prev => !prev)
      else if (typeof onClick === 'function') onClick(!value)
    }, [value, onClick])

    useImperativeHandle(ref, () => ({ value: state }))

    return (
      <Button
        id={id}
        className={className}
        onClick={onClickHandler}
        dataSet={dataSet}
        disabled={disabled}
        tabbable={tabbable}
        title={title}
        onClickBlur={onClickBlur}
        onFocus={onFocus}
      >
        <Icon active={computedState} strokeWidth={5} />
        {label}
      </Button>
    )
  })
)

RadioButton.displayName = 'Casus-Components-RadioButton'

export default RadioButton
